import React from 'react';

import { Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconCurrencyDollar, IconCurrencyDollarOff } from '@tabler/icons-react';

import { formatUtcDate } from '@/utils/formatDate';

import { ReportInfo } from '@/types/patientList';

import styles from './cellsV2.module.scss';
import { ReportName } from './ReportName';

interface PreviousReportCellProps {
  report: ReportInfo | null;
}

function BillableMark({ billable }: { billable?: boolean | null }) {
  if (typeof billable !== 'boolean') return null;
  const Icon = billable ? IconCurrencyDollar : IconCurrencyDollarOff;
  return (
    <Tooltip label={billable ? 'Billable' : 'Non-billable'} withArrow>
      <span style={{ display: 'inline-flex' }} aria-label={billable ? 'Billable' : 'Non-billable'}>
        <Icon
          size={14}
          stroke={1.8}
          color={billable ? 'var(--mantine-color-teal-7)' : 'var(--mantine-color-gray-6)'}
        />
      </span>
    </Tooltip>
  );
}

const completedStatusText = (report: ReportInfo) => {
  switch (report.signatureStatus) {
    case 'SIGNED':
      return 'Completed';
    case 'DISMISSED':
      return 'Dismissed';
    default:
      return 'Completed';
  }
};

export const PreviousReportCell: React.FC<PreviousReportCellProps> = ({ report }) => {
  if (!report)
    return (
      <Text className={styles.muted} fz={14}>
        —
      </Text>
    );

  return (
    <Stack gap={2}>
      <ReportName report={report} />
      <Group gap={6} wrap="nowrap">
        <Text className={styles.statusChip} fz={12}>
          {completedStatusText(report)}
        </Text>
        <BillableMark billable={report.isBillable} />
      </Group>
      <Text className={styles.sub} fz={12}>
        DOS {report.billingDate ? formatUtcDate(report.billingDate, 'MM-DD-YYYY') : 'N/A'}
      </Text>
    </Stack>
  );
};
